import {
  ArrowRight,
  Building2,
  CarFront,
  CarTaxiFront,
  CircleAlert,
  Clock3,
  FileClock,
  TrendingUp,
} from "lucide-react";
import { Link } from "react-router-dom";
import { Badge, Card, EmptyState, Skeleton } from "../components/ui";
import { DataTable } from "../components/DataTable";
import { useDashboardData } from "../data/platform-data";
import { useAssetDashboard } from "../data/assets-platform-data";
import { useTenantRuntime } from "../config/TenantRuntime";
import { FlowOverviewPage } from "./FlowOverviewPage";

function tone(status: string): "success" | "warning" | "info" | "danger" {
  if (status === "ACTIVE" || status === "ATIVO") return "success";
  if (status === "OVERDUE" || status === "VENCIDO") return "danger";
  if (status.includes("PAYMENT") || status.includes("PAGAMENTO")) return "warning";
  return "info";
}

function AssetDashboard() {
  const dashboard = useAssetDashboard();
  if (dashboard.isLoading) return <Skeleton className="dashboard-skeleton" />;
  if (dashboard.error) {
    return (
      <EmptyState
        title="Não foi possível carregar o patrimônio"
        description={dashboard.error.message}
      />
    );
  }
  const data = dashboard.data ?? { metrics: [], sales: [], receivables: [] };

  return (
    <div className="page-stack">
      <div className="notice">
        <CircleAlert size={20} />
        <div>
          <strong>Produto Patrimônio e Recebíveis</strong>
          <p>
            Veículos, imóveis, locações, vendas parceladas e segunda via usam o mesmo motor
            financeiro da Yatta.
          </p>
        </div>
      </div>

      <section className="metric-grid">
        {data.metrics.map((metric) => (
          <article className="metric-card" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <small>{metric.delta}</small>
          </article>
        ))}
      </section>

      <div className="three-feature-grid">
        <Link className="feature-navigation-card" to="/vendas-veiculos">
          <CarFront size={23} />
          <div>
            <strong>Venda de veículos</strong>
            <span>À vista e parcelada</span>
          </div>
          <ArrowRight size={17} />
        </Link>
        <Link className="feature-navigation-card" to="/locacoes-imoveis">
          <Building2 size={23} />
          <div>
            <strong>Locação de imóveis</strong>
            <span>40 contratos estimados</span>
          </div>
          <ArrowRight size={17} />
        </Link>
        <Link className="feature-navigation-card" to="/locacoes-veiculos">
          <CarTaxiFront size={23} />
          <div>
            <strong>Locação para motoristas</strong>
            <span>30 veículos estimados</span>
          </div>
          <ArrowRight size={17} />
        </Link>
      </div>

      <div className="two-column">
        <Card
          title="Vendas parceladas recentes"
          description="Contratos, saldos e situação da transferência"
          action={
            <Link className="text-link" to="/vendas-veiculos">
              Ver todas <ArrowRight size={15} />
            </Link>
          }
        >
          <DataTable
            rows={data.sales}
            getKey={(sale) => sale.id}
            columns={[
              { key: "customer", header: "Cliente", render: (sale) => sale.customer },
              { key: "vehicle", header: "Veículo", render: (sale) => sale.vehicle },
              {
                key: "status",
                header: "Status",
                render: (sale) => <Badge tone={tone(sale.status)}>{sale.status}</Badge>,
              },
              { key: "total", header: "Venda", render: (sale) => sale.total },
            ]}
          />
        </Card>

        <Card title="Atenções operacionais" description="Fila priorizada">
          <div className="task-list">
            <div className="task-item">
              <FileClock size={18} />
              <div>
                <strong>
                  {data.receivables.filter((item) => item.status === "OVERDUE").length} parcelas
                  vencidas
                </strong>
                <span>Valores atualizados para segunda via</span>
              </div>
            </div>
            <div className="task-item">
              <Clock3 size={18} />
              <div>
                <strong>12 transferências pendentes</strong>
                <span>Vendas de veículos ainda em processamento</span>
              </div>
            </div>
            <div className="task-item">
              <TrendingUp size={18} />
              <div>
                <strong>Portal de segunda via preparado</strong>
                <span>CPF/CNPJ + OTP + Pix ou boleto</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function PlansDashboard() {
  const data = useDashboardData();
  if (data.loading) return <Skeleton className="dashboard-skeleton" />;
  if (data.error) {
    return (
      <EmptyState title="Não foi possível carregar o painel" description={data.error.message} />
    );
  }

  return (
    <div className="page-stack">
      <div className="notice">
        <CircleAlert size={20} />
        <div>
          <strong>Ambiente controlado</strong>
          <p>O modo de cada integração aparece no painel de saúde.</p>
        </div>
      </div>
      <section className="metric-grid">
        {data.metrics.map((metric) => (
          <article className="metric-card" key={metric.label}>
            <span>{metric.label}</span>
            <strong>{metric.value}</strong>
            <small>{metric.delta}</small>
          </article>
        ))}
      </section>
      <div className="two-column">
        <Card
          title="Vendas recentes"
          description="Origem comercial preservada desde a EVA"
          action={
            <Link className="text-link" to="/vendas">
              Ver todas <ArrowRight size={15} />
            </Link>
          }
        >
          <DataTable
            rows={data.sales}
            getKey={(sale) => sale.id}
            columns={[
              { key: "customer", header: "Cliente", render: (sale) => sale.customer },
              { key: "offer", header: "Oferta", render: (sale) => sale.offer },
              {
                key: "status",
                header: "Status",
                render: (sale) => <Badge tone={tone(sale.status)}>{sale.status}</Badge>,
              },
              { key: "amount", header: "Valor", render: (sale) => sale.amount },
            ]}
          />
        </Card>
        <Card title="Atenções operacionais" description="Fila priorizada para a equipe">
          <div className="task-list">
            <div className="task-item">
              <Clock3 size={18} />
              <div>
                <strong>Assinaturas pendentes</strong>
                <span>Acompanhe envelopes em aberto</span>
              </div>
            </div>
            <div className="task-item">
              <CircleAlert size={18} />
              <div>
                <strong>Cobranças vencidas</strong>
                <span>A EVA pode executar a comunicação</span>
              </div>
            </div>
            <div className="task-item">
              <TrendingUp size={18} />
              <div>
                <strong>Ativações de benefícios</strong>
                <span>Funeral e telemedicina independentes</span>
              </div>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

export function DashboardPage() {
  const { hasModule, tenant } = useTenantRuntime();
  if (tenant.product.commercialProductCode === "EVA_FLOW") return <FlowOverviewPage />;
  return hasModule("assets") ? <AssetDashboard /> : <PlansDashboard />;
}
